import type { EmailCampaignPreviewCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { ChoiceAnswer } from './message-choices';
import type { ReviewComment, SentReviewComment } from './site-review';

export interface EmailCampaignPreviewCardData extends Required<Pick<EmailCampaignPreviewCardMetadata, 'metaSiteId' | 'campaignId'>> {
  subject: string;
  decisionId: string;
  taskId: string;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_SUBJECT = 1000;
const MAX_ID = 128;
const MAX_TEXT = 200;
const MAX_TAG = 40;
const MAX_SELECTOR = 1000;

function text(value: unknown, cap: number): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, cap) : '';
}

export function readEmailCampaignPreviewCard(metadata: unknown): EmailCampaignPreviewCardData | null {
  const raw = (metadata as { emailCampaignPreviewCard?: unknown } | null | undefined)?.emailCampaignPreviewCard;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const card = raw as Record<string, unknown>;
  const metaSiteId = text(card.metaSiteId, MAX_ID);
  const campaignId = text(card.campaignId, MAX_ID);
  if (!GUID.test(metaSiteId) || !GUID.test(campaignId)) return null;
  return {
    metaSiteId,
    campaignId,
    subject: text(card.subject, MAX_SUBJECT),
    decisionId: text(card.decisionId, MAX_ID),
    taskId: text(card.taskId, MAX_ID),
  };
}

type EmailCardChoices = { askId: string; options: readonly { id: string }[] } | undefined;

function isHeldEmailCard(card: EmailCampaignPreviewCardData, choices: EmailCardChoices): choices is NonNullable<EmailCardChoices> {
  return Boolean(card.decisionId) && choices?.askId === `decision:${card.decisionId}`;
}

export function emailReviewEditChoice(card: EmailCampaignPreviewCardData, choices: EmailCardChoices): ChoiceAnswer | null {
  if (!isHeldEmailCard(card, choices)) return null;
  const optionId = `da:${card.decisionId}:edit-campaign`;
  return choices.options.some((o) => o.id === optionId) ? { askId: choices.askId, optionId } : null;
}

export type EmailReviewMode = 'edit-first' | 'comment' | null;

export function emailReviewMode(
  card: EmailCampaignPreviewCardData,
  choices: EmailCardChoices,
  { latest, answered }: { latest: boolean; answered: boolean },
): EmailReviewMode {
  if (!latest) return null;
  if (!isHeldEmailCard(card, choices)) return 'comment';
  return !answered && emailReviewEditChoice(card, choices) ? 'edit-first' : null;
}

function emailReviewSend(
  mode: EmailReviewMode,
  card: EmailCampaignPreviewCardData,
  choices: EmailCardChoices,
): { choiceAnswer?: ChoiceAnswer } | null {
  if (mode === 'comment') return {};
  const choiceAnswer = mode === 'edit-first' ? emailReviewEditChoice(card, choices) : null;
  return choiceAnswer ? { choiceAnswer } : null;
}

interface EmailCardRow {
  id?: string;
  finalId?: string;
  emailCampaignPreviewCard?: EmailCampaignPreviewCardData;
  choices?: EmailCardChoices;
}

const rowKey = (row: EmailCardRow): string | undefined => row.finalId ?? row.id;
const campaignKey = (card: EmailCampaignPreviewCardData): string => card.campaignId.toLowerCase();

export function latestEmailCards<T extends EmailCardRow>(rows: readonly T[]): Map<string, T> {
  const latest = new Map<string, T>();
  for (const row of rows) {
    if (row.emailCampaignPreviewCard) latest.set(campaignKey(row.emailCampaignPreviewCard), row);
  }
  return latest;
}

export function latestEmailCard<T extends EmailCardRow>(latest: ReadonlyMap<string, T>, card: EmailCampaignPreviewCardData): T | undefined {
  return latest.get(campaignKey(card));
}

export function isLatestEmailCard(latest: ReadonlyMap<string, EmailCardRow>, row: EmailCardRow): boolean {
  const key = rowKey(row);
  const last = row.emailCampaignPreviewCard ? latestEmailCard(latest, row.emailCampaignPreviewCard) : undefined;
  return key !== undefined && last !== undefined && rowKey(last) === key;
}

export type EmailReviewSendPlan<T> =
  | { status: 'send'; row: T; choiceAnswer?: ChoiceAnswer }
  | { status: 'busy' }
  | { status: 'answered' }
  | { status: 'newer'; row: T; card: EmailCampaignPreviewCardData };

export function planEmailReviewSend<T extends EmailCardRow>(
  opened: T,
  latest: ReadonlyMap<string, T>,
  { streaming, modeFor }: { streaming: boolean; modeFor: (row: T) => EmailReviewMode },
): EmailReviewSendPlan<T> {
  const row = opened.emailCampaignPreviewCard ? latestEmailCard(latest, opened.emailCampaignPreviewCard) : undefined;
  const card = row?.emailCampaignPreviewCard;
  const send = row && card ? emailReviewSend(modeFor(row), card, row.choices) : null;
  if (!row || !card || !send) return { status: 'answered' };
  if (!isLatestEmailCard(latest, opened)) return { status: 'newer', row, card };
  if (streaming) return { status: 'busy' };
  return { status: 'send', row, ...send };
}

export function emailReviewPageUrl(campaignId: string): string {
  return `https://email-review.invalid/${encodeURIComponent(campaignId)}`;
}

export function emailReviewKey(campaignId: string, conversationId: string | null): string {
  return `email:${campaignId.toLowerCase()}|${conversationId ?? ''}`;
}

export function workspaceForSite(
  workspaces: readonly { id?: string; website?: { metaSiteId?: string } }[],
  metaSiteId: string,
): string | null {
  const own = workspaces.find((w) => w.id && w.website?.metaSiteId === metaSiteId);
  return own?.id ?? workspaces.find((w) => w.id)?.id ?? null;
}

export const EMAIL_REVIEW_LEAD = 'Address all comments on the email';

const SITE_LINE = /^Site: (\S+)$/;
const CAMPAIGN_LINE = /^Campaign: (\S+)$/;
const SUBJECT_LINE = /^Subject: .*$/;
const ELEMENT_LINE = /^(\d+)\. Element: (\S+)(?: "([^"]*)")? \((.*)\)$/;
const COMMENT_LINE = /^ {3}Comment: (.*)$/;

export function emailReviewMessage(
  campaign: { metaSiteId: string; campaignId: string; subject: string },
  comments: readonly ReviewComment[],
): string {
  const subject = text(campaign.subject, MAX_SUBJECT);
  const head = [EMAIL_REVIEW_LEAD, `Site: ${campaign.metaSiteId}`, `Campaign: ${campaign.campaignId}`, ...(subject ? [`Subject: ${subject}`] : [])];
  const blocks = comments.map((c, i) => {
    const quoted = text(c.text, MAX_TEXT).replace(/"/g, "'");
    const tag = text(c.tag, MAX_TAG) || 'element';
    const selector = text(c.selector, MAX_SELECTOR);
    const element = quoted ? `${tag} "${quoted}" (${selector})` : `${tag} (${selector})`;
    const [first = '', ...rest] = c.comment.replace(/\r\n?/g, '\n').trim().split('\n');
    return [`${i + 1}. Element: ${element}`, `   Comment: ${first}`, ...rest.map((line) => `   ${line}`)].join('\n');
  });
  return [head.join('\n'), ...blocks].join('\n\n');
}

export function parseSentEmailReview(message: string): SentReviewComment[] | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  if (lines[0]?.trim() !== EMAIL_REVIEW_LEAD || !SITE_LINE.test(lines[1] ?? '') || !CAMPAIGN_LINE.test(lines[2] ?? '')) return null;
  let start = 3;
  if (SUBJECT_LINE.test(lines[start] ?? '')) start += 1;
  if (lines[start] !== '') return null;
  const comments: SentReviewComment[] = [];
  let i = start + 1;
  while (i < lines.length) {
    const element = ELEMENT_LINE.exec(lines[i]);
    const first = COMMENT_LINE.exec(lines[i + 1] ?? '');
    if (!element || Number(element[1]) !== comments.length + 1 || !first) return null;
    const comment = [first[1]];
    i += 2;
    while (i < lines.length && lines[i] !== '') {
      if (!lines[i].startsWith('   ')) return null;
      comment.push(lines[i].slice(3));
      i += 1;
    }
    comments.push({ pageUrl: '', tag: element[2], text: element[3] ?? '', selector: element[4], comment: comment.join('\n') });
    if (i < lines.length) i += 1;
  }
  return comments.length > 0 ? comments : null;
}

export interface SentReviewRound {
  id: string;
  comments: SentReviewComment[];
}

export function readSentEmailReview(message: string): SentReviewRound | null {
  const comments = parseSentEmailReview(message);
  const id = comments ? CAMPAIGN_LINE.exec(message.replace(/\r\n?/g, '\n').trim().split('\n')[2] ?? '')?.[1] : undefined;
  return comments && id ? { id: id.toLowerCase(), comments } : null;
}

export function reviewRounds<T extends { id?: string; finalId?: string; role?: string; content?: string }>(
  rows: readonly T[],
  cardId: (row: T) => string | undefined,
  readSent: (message: string) => SentReviewRound | null,
): Map<string, SentReviewComment[]> {
  const open = new Map<string, string>();
  const rounds = new Map<string, SentReviewComment[]>();
  for (const row of rows) {
    const id = cardId(row)?.toLowerCase();
    if (id) {
      const key = row.finalId ?? row.id;
      if (key) open.set(id, key);
      else open.delete(id);
      continue;
    }
    const sent = row.role === 'USER' && row.content ? readSent(row.content) : null;
    const card = sent ? open.get(sent.id) : undefined;
    if (!sent || !card) continue;
    rounds.set(card, sent.comments);
    open.delete(sent.id);
  }
  return rounds;
}

export function reviewNonce(random: (bytes: Uint8Array) => Uint8Array = (bytes) => crypto.getRandomValues(bytes)): string {
  return Array.from(random(new Uint8Array(24)), (b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36]).join('');
}

function hardenedEmailDocument(html: string, scriptSrc: string, parser: DOMParser): Document {
  const doc = parser.parseFromString(html, 'text/html');
  doc.querySelectorAll('meta[http-equiv], base').forEach((el) => {
    const name = (el.getAttribute('http-equiv') ?? '').trim().toLowerCase();
    if (el.tagName === 'BASE' || name === 'refresh' || name === 'content-security-policy') el.remove();
  });
  doc.querySelectorAll('meta[name], noscript').forEach((el) => {
    if (el.tagName === 'NOSCRIPT' || (el.getAttribute('name') ?? '').trim().toLowerCase() === 'referrer') el.remove();
  });
  doc.querySelectorAll('[referrerpolicy]').forEach((el) => el.removeAttribute('referrerpolicy'));
  const policy = doc.createElement('meta');
  policy.setAttribute('http-equiv', 'Content-Security-Policy');
  policy.setAttribute('content', `script-src ${scriptSrc}; object-src 'none'; frame-src 'none'; base-uri 'none'; form-action 'none'`);
  const referrer = doc.createElement('meta');
  referrer.setAttribute('name', 'referrer');
  referrer.setAttribute('content', 'no-referrer');
  doc.head.prepend(policy, referrer);
  return doc;
}

export interface ReviewBridgeOptions {
  areas?: readonly string[];
  fit?: boolean;
}

export function emailReviewDocument(
  html: string,
  pageUrl: string,
  nonce: string,
  options: ReviewBridgeOptions = {},
  parser: DOMParser = new DOMParser(),
): string {
  const doc = hardenedEmailDocument(html, `'nonce-${nonce}'`, parser);
  const bridge = doc.createElement('script');
  bridge.setAttribute('nonce', nonce);
  bridge.textContent = emailReviewBridge(pageUrl, options);
  doc.body.append(bridge);
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

function anchoredElement(doc: Document, comment: SentReviewComment): Element | null {
  let element: Element | null = null;
  try {
    element = doc.querySelector(comment.selector);
  } catch {
    return null;
  }
  if (!element || element.tagName.toLowerCase() !== comment.tag.trim().toLowerCase()) return null;
  if (!comment.text) return element;
  const own = [element.textContent, element.getAttribute('alt'), element.getAttribute('aria-label')]
    .map((value) => (value ?? '').replace(/\s+/g, ' ').replace(/"/g, "'").toLowerCase())
    .join(' ');
  return own.includes(comment.text.toLowerCase()) ? element : null;
}

const LINK_ATTRIBUTES = ['href', 'xlink:href', 'target', 'ping'];
const FORM_ATTRIBUTES = ['action', 'formaction'];
function makeInert(doc: Document, css: string): void {
  doc.querySelectorAll('a, area').forEach((el) => LINK_ATTRIBUTES.forEach((name) => el.removeAttribute(name)));
  doc.querySelectorAll('[action], [formaction]').forEach((el) => FORM_ATTRIBUTES.forEach((name) => el.removeAttribute(name)));
  if (!css) return;
  const style = doc.createElement('style');
  style.textContent = css;
  doc.head.append(style);
}

const PIN_COLOR = '#ff6b00';

const PINS_SCRIPT = `(function(){var D=document,boxes=[];
function draw(){boxes.forEach(function(b){var r=b[1].getBoundingClientRect(),s=b[0].style;s.left=(r.left+scrollX)+"px";s.top=(r.top+scrollY)+"px";s.width=r.width+"px";s.height=r.height+"px";s.display=r.width||r.height?"block":"none"})}
D.querySelectorAll("[data-symphony-pin]").forEach(function(el){var d=D.createElement("div"),b=D.createElement("span");d.style.cssText="position:absolute;pointer-events:none;z-index:2147483646;box-sizing:border-box;border:2px solid ${PIN_COLOR};border-radius:4px;display:none";b.textContent=el.getAttribute("data-symphony-pin");b.style.cssText="position:absolute;left:-2px;top:-20px;padding:0 5px;border-radius:9px;background:${PIN_COLOR};color:#fff;font:600 11px/18px sans-serif;white-space:nowrap";d.appendChild(b);D.documentElement.appendChild(d);boxes.push([d,el])});
draw();addEventListener("load",draw);addEventListener("resize",draw);if(window.ResizeObserver)new ResizeObserver(draw).observe(D.documentElement)})();`;

export function reviewedViewDocument(
  html: string,
  comments: readonly SentReviewComment[],
  { heading, describe }: { heading: string; describe: (comment: SentReviewComment) => string },
  nonce: string,
  parser: DOMParser = new DOMParser(),
): string {
  const doc = hardenedEmailDocument(html, `'nonce-${nonce}'`, parser);
  makeInert(doc, '');
  const anchored = comments.map((comment) => anchoredElement(doc, comment));
  const labels = new Map<Element, string[]>();
  anchored.forEach((element, i) => {
    if (element) labels.set(element, [...(labels.get(element) ?? []), String(i + 1)]);
  });
  labels.forEach((numbers, element) => element.setAttribute('data-symphony-pin', numbers.join(', ')));

  const list = doc.createElement('aside');
  list.setAttribute('data-symphony-comments', '');
  list.style.cssText = 'margin:0;padding:12px 16px;background:#fff7f0;border-bottom:1px solid #f3d9c4;color:#1f1f1f;font:14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;text-align:start';
  const title = doc.createElement('div');
  title.textContent = heading;
  title.style.cssText = 'font:600 14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;margin-bottom:8px';
  const items = doc.createElement('ol');
  items.style.cssText = 'margin:0;padding:0;list-style:none;display:flex;flex-direction:column;gap:8px';
  comments.forEach((comment, i) => {
    const item = doc.createElement('li');
    item.style.cssText = 'display:flex;gap:8px;align-items:flex-start';
    const number = doc.createElement('span');
    number.textContent = String(i + 1);
    number.style.cssText = `flex:none;min-width:18px;padding:0 5px;border-radius:9px;text-align:center;font:600 11px/18px sans-serif;${
      anchored[i] ? `background:${PIN_COLOR};color:#fff` : 'background:#e5e5e5;color:#555'
    }`;
    const body = doc.createElement('span');
    body.style.cssText = 'min-width:0;display:flex;flex-direction:column;gap:2px';
    const said = doc.createElement('span');
    said.dir = 'auto';
    said.textContent = comment.comment;
    said.style.cssText = 'white-space:pre-wrap;overflow-wrap:anywhere';
    const where = doc.createElement('span');
    where.dir = 'auto';
    where.textContent = describe(comment);
    where.style.cssText = 'color:#6b6b6b;font-size:12px;overflow-wrap:anywhere';
    body.append(said, where);
    item.append(number, body);
    items.append(item);
  });
  list.append(title, items);
  doc.body.prepend(list);

  const pins = doc.createElement('script');
  pins.setAttribute('nonce', nonce);
  pins.textContent = PINS_SCRIPT;
  doc.body.append(pins);
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

function inertDocument(html: string, css: string, parser: DOMParser): string {
  const doc = hardenedEmailDocument(html, "'none'", parser);
  doc.querySelectorAll('script').forEach((el) => el.remove());
  makeInert(doc, css);
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

export function emailViewDocument(html: string, parser: DOMParser = new DOMParser()): string {
  return `<!doctype html>${hardenedEmailDocument(html, "'none'", parser).documentElement.outerHTML}`;
}

export function readOnlyViewDocument(html: string, parser: DOMParser = new DOMParser()): string {
  return inertDocument(html, '', parser);
}

export function siteViewDocument(html: string, nonce: string, parser: DOMParser = new DOMParser()): string {
  const doc = hardenedEmailDocument(html, `'nonce-${nonce}'`, parser);
  doc.querySelectorAll('script').forEach((el) => el.remove());
  makeInert(doc, '');
  const images = doc.createElement('meta');
  images.setAttribute('http-equiv', 'Content-Security-Policy');
  images.setAttribute('content', 'img-src https: data:');
  doc.head.prepend(images);
  const fit = doc.createElement('script');
  fit.setAttribute('nonce', nonce);
  fit.textContent = `(function(){var D=document;${FIT_PAGE}fz();addEventListener("resize",fz);addEventListener("load",fz)})();`;
  doc.body.append(fit);
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

export function inlinePreviewDocument(html: string, parser: DOMParser = new DOMParser()): string {
  return inertDocument(html, 'html{overflow:hidden}', parser);
}

export function emailViewFileName(subject: string): string {
  const name = text(subject, 120).replace(/[\\/:*?"<>|]/g, '').trim();
  return `${name || 'Email'}.html`;
}

const FIT_PAGE =
  'function fz(){var s=D.body&&D.body.style;if(!s)return;s.zoom="";var w=D.documentElement.scrollWidth,c=D.documentElement.clientWidth;if(w>c&&c>0)s.zoom=String(c/w)}';

export function emailReviewBridge(pageUrl: string, { areas = [], fit = false }: ReviewBridgeOptions = {}): string {
  return `(function(){
var W=window,D=document,K="symphony-review",U=K+"-ui",PAGE=${JSON.stringify(pageUrl)},A=${JSON.stringify(areas.join(','))},FIT=${fit},port=null,pk=true,box=null,hv=null,pins=[],raf=0,R=Math.round;
${FIT_PAGE}
if(FIT){fz();addEventListener("resize",fz);addEventListener("load",fz)}
function sn(m){m.type=K+":"+m.type;try{if(port)port.postMessage(m)}catch(e){}}
function mk(c){var d=D.createElement("div");d.setAttribute(U,"");d.style.cssText="position:fixed;pointer-events:none;z-index:2147483646;box-sizing:border-box;border:2px solid "+c+";border-radius:4px;display:none";D.documentElement.appendChild(d);return d}
function pl(d,el){var r=el.getBoundingClientRect(),s=d.style;s.left=r.left+"px";s.top=r.top+"px";s.width=r.width+"px";s.height=r.height+"px";s.display=r.width||r.height?"block":"none"}
function re(){if(!raf)raf=requestAnimationFrame(function(){raf=0;pins.forEach(function(p){pl(p[0],p[1])});if(hv&&box.style.display==="block")pl(box,hv)})}
function sel(el){var p=[],i,k,u,s,t;while(el&&el.nodeType===1&&el!==D.body&&el!==D.documentElement){if(el.id&&D.querySelectorAll("#"+CSS.escape(el.id)).length===1){p.unshift("#"+CSS.escape(el.id));break}t=el.tagName;i=1;k=1;u=0;s=el;while((s=s.previousElementSibling)){k++;if(s.tagName===t)i++}s=el;while(!u&&(s=s.nextElementSibling))if(s.tagName===t)u=1;p.unshift(/[^A-Z0-9-]/.test(t)?"*:nth-child("+k+")":t.toLowerCase()+(i>1||u?":nth-of-type("+i+")":""));el=el.parentElement}if(el===D.body)p.unshift("body");t=p.join(" > ");if(t.length>1000)for(i=p.length-1;i>0;i--){s=p.slice(i).join(" > ");try{if(D.querySelectorAll(s).length===1)return s}catch(e){}}return t}
function one(el){var c=null,k=el.childNodes,i,n;for(i=0;i<k.length;i++){n=k[i];if(n.nodeType===3&&n.nodeValue.trim())return null;if(n.nodeType===1&&!n.hasAttribute(U)&&n.tagName!=="SCRIPT"&&n.tagName!=="STYLE"){if(c)return null;c=n}}return c}
function tgt(e){var t=e.target,c;if(!t||t.nodeType!==1||t.hasAttribute(U)||(A&&!t.closest(A)))return null;while((c=one(t)))t=c;return t}
function cl(){pins.forEach(function(p){p[0].remove()});pins=[]}
function rv(m){if(!m||typeof m.type!=="string")return;var t=m.type.slice(K.length+1);
if(t==="mode"){pk=!!m.picking;box.style.display="none"}else if(t==="clear")cl();
else if(t==="highlight"){cl();(m.pins||(m.selector?[{selector:m.selector}]:[])).forEach(function(p,i){var el=null;try{el=D.querySelector(p.selector)}catch(e){}if(!el)return;var d=mk("#ff6b00");if(p.label!=null){var b=D.createElement("span");b.textContent=String(p.label).slice(0,4);b.style.cssText="position:absolute;left:-2px;top:-20px;padding:0 5px;border-radius:9px;background:#ff6b00;color:#fff;font:600 11px/18px sans-serif";d.appendChild(b)}pins.push([d,el]);if(!i&&m.scroll)el.scrollIntoView({block:"center"});pl(d,el)})}}
function go(){box=mk("#116dff");addEventListener("scroll",re,true);addEventListener("resize",re);
D.addEventListener("mouseover",function(e){var t=pk&&tgt(e);if(t){hv=t;pl(box,t)}else if(A){hv=null;box.style.display="none"}},true);
D.addEventListener("click",function(e){e.preventDefault();if(!pk)return;e.stopImmediatePropagation();var t=tgt(e);if(!t)return;hv=t;pl(box,t);var r=t.getBoundingClientRect(),x=(t.innerText||t.textContent||t.getAttribute("alt")||t.getAttribute("aria-label")||"").replace(/\\s+/g," ").trim().slice(0,200);
sn({type:"pick",pageUrl:PAGE,selector:sel(t),text:x,tag:t.tagName.toLowerCase(),rect:{x:R(r.left),y:R(r.top),w:R(r.width),h:R(r.height)},viewport:{w:innerWidth,h:innerHeight},devicePixelRatio:W.devicePixelRatio||1})},true)}
function stop(e){e.preventDefault()}D.addEventListener("click",stop,true);D.addEventListener("auxclick",stop,true);D.addEventListener("submit",stop,true);
addEventListener("message",function(e){if(port||e.source!==W.parent||!e.data||e.data.type!==K+":connect"||!e.ports||!e.ports[0])return;port=e.ports[0];port.onmessage=function(m){rv(m.data)};var start=function(){go();sn({type:"ready",pageUrl:PAGE,title:D.title})};if(D.readyState==="loading")D.addEventListener("DOMContentLoaded",start);else start()});
})();`;
}
